import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { AlertTriangle, Loader2, RotateCcw } from 'lucide-react';
import { paymentApi, type Payment, type RefundReason } from '@/lib/api';
import { formatAmount } from '@/lib/currency';

interface RefundModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  payment: Payment;
  onRefundSuccess: (refund: Payment) => void;
}

const REFUND_REASONS: { value: RefundReason; label: string }[] = [
  { value: 'requested_by_customer', label: 'Requested by Customer' },
  { value: 'duplicate', label: 'Duplicate Charge' },
  { value: 'fraudulent', label: 'Fraudulent' },
  { value: 'other', label: 'Other' },
];

export function RefundModal({
  open,
  onOpenChange,
  payment,
  onRefundSuccess,
}: RefundModalProps) {
  const [refundType, setRefundType] = useState<'full' | 'partial'>('full');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState<RefundReason>('requested_by_customer');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const refundableAmount = parseFloat(payment.refundable_amount);
  const originalAmount = parseFloat(payment.amount);
  const totalRefunded = parseFloat(payment.total_refunded);
  const isACH = payment.stripe_payment_method === 'ach';

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setRefundType('full');
      setAmount(payment.refundable_amount);
      setReason('requested_by_customer');
      setNotes('');
      setError('');
    }
  }, [open, payment.refundable_amount]);

  // Update amount when refund type changes
  useEffect(() => {
    if (refundType === 'full') {
      setAmount(payment.refundable_amount);
    }
  }, [refundType, payment.refundable_amount]);

  const handleSubmit = async () => {
    const refundAmount = parseFloat(amount);

    // Validation
    if (isNaN(refundAmount) || refundAmount <= 0) {
      setError('Please enter a valid refund amount.');
      return;
    }

    if (refundAmount > refundableAmount) {
      setError(`Refund amount cannot exceed ${formatAmount(refundableAmount)}.`);
      return;
    }

    setIsSubmitting(true);
    setError('');

    const response = await paymentApi.refund(payment.id, {
      amount: refundAmount.toFixed(2),
      reason,
      notes,
    });

    setIsSubmitting(false);

    if (response.error) {
      setError(response.error);
      return;
    }

    if (response.data) {
      onRefundSuccess(response.data);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100">
              <RotateCcw className="h-5 w-5 text-amber-600" />
            </div>
            <DialogTitle>Refund Payment</DialogTitle>
          </div>
          <DialogDescription>
            Process a refund for this Stripe payment.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Payment Summary */}
          <div className="rounded-lg border bg-muted/30 p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Original Payment</span>
              <span className="font-medium">{formatAmount(originalAmount)}</span>
            </div>
            {totalRefunded > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Already Refunded</span>
                <span className="font-medium text-red-600">-{formatAmount(totalRefunded)}</span>
              </div>
            )}
            <div className="flex justify-between text-sm border-t pt-2">
              <span className="text-muted-foreground">Maximum Refundable</span>
              <span className="font-semibold">{formatAmount(refundableAmount)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Payment Date</span>
              <span>{new Date(payment.payment_date).toLocaleDateString()}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Method</span>
              <span className="capitalize">{payment.stripe_payment_method || 'Card'}</span>
            </div>
          </div>

          {/* ACH Warning */}
          {isACH && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 flex gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <strong>Bank transfer refunds take 3-5 business days</strong> to process.
                You'll be notified when the refund completes.
              </div>
            </div>
          )}

          {/* Refund Type */}
          <div className="space-y-3">
            <Label>Refund Amount</Label>
            <RadioGroup
              value={refundType}
              onValueChange={(value) => setRefundType(value as 'full' | 'partial')}
              className="flex flex-col gap-3"
            >
              <div className="flex items-center space-x-3">
                <RadioGroupItem value="full" id="full" />
                <Label htmlFor="full" className="font-normal cursor-pointer">
                  Full refund ({formatAmount(refundableAmount)})
                </Label>
              </div>
              <div className="flex items-center space-x-3">
                <RadioGroupItem value="partial" id="partial" />
                <Label htmlFor="partial" className="font-normal cursor-pointer">
                  Partial refund
                </Label>
              </div>
            </RadioGroup>

            {refundType === 'partial' && (
              <div className="pl-7">
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0.01"
                    max={refundableAmount}
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="pl-7"
                    placeholder="0.00"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Reason */}
          <div className="space-y-2">
            <Label htmlFor="reason">Reason for Refund</Label>
            <Select value={reason} onValueChange={(value) => setReason(value as RefundReason)}>
              <SelectTrigger id="reason">
                <SelectValue placeholder="Select a reason" />
              </SelectTrigger>
              <SelectContent>
                {REFUND_REASONS.map((r) => (
                  <SelectItem key={r.value} value={r.value}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Internal Notes (Optional)</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any notes about this refund..."
              rows={2}
            />
          </div>

          {/* Error */}
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              {error}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting} variant="destructive">
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : (
              <>Refund {formatAmount(parseFloat(amount) || 0)}</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
